import { useEffect, useMemo, useState } from "react";
import type {
  AdminLessonListItemDto,
  AdminRosterRelationsResponseDto,
  AdminStudentsResponseDto,
  EnrollmentsResponseDto,
  FamilyAccountStatus,
  FamilyAccountsResponseDto
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminLessons } from "../../shared/api/adminLesson";
import {
  bulkUpdateFamilyAccountStatus,
  fetchAdminEnrollments,
  fetchAdminRosterRelations,
  fetchAdminStudents,
  fetchFamilyAccounts
} from "../../shared/api/adminRoster";
import { adminSearchMatches } from "../../shared/lib/adminSearch";
import { buildCsvContent, downloadCsv, formatTenantDateForCsvFilename } from "../../shared/lib/csvExport";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { getProtectedApiErrorMessage, isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { AdminBulkActionBar } from "../../shared/ui/admin-list/AdminBulkActionBar";
import { AdminDataGrid, type AdminDataGridColumn } from "../../shared/ui/admin-list/AdminDataGrid";
import { AdminFilterPanel } from "../../shared/ui/admin-list/AdminFilterPanel";
import { AdminListToolbar } from "../../shared/ui/admin-list/AdminListToolbar";
import { AdminPagination } from "../../shared/ui/admin-list/AdminPagination";
import { useAdminListState } from "../../shared/ui/admin-list/useAdminListState";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import {
  buildAdminRosterLinks,
  buildFamilyAccountRows,
  buildLessonFilterOptions,
  compareAdminDate,
  compareAdminText,
  formatAdminDate,
  formatGuardianNames,
  formatRosterPrimaryContact,
  summarizeNames,
  type AdminFamilyAccountListRow
} from "./AdminRosterListShared";

type FamilyAccountSortKey =
  | "displayName"
  | "primaryContactDisplayName"
  | "studentCount"
  | "guardianCount"
  | "status"
  | "updatedAt";

type FamilyAccountFilters = {
  status: "all" | FamilyAccountStatus;
  primaryContact: "all" | "set" | "unset" | "notLinkedToAnyStudent";
  lessonId: "all" | string;
  students: "all" | "has" | "none";
  guardian: "all" | "linked" | "unlinked";
};

const familyAccountSortKeys: FamilyAccountSortKey[] = [
  "displayName",
  "primaryContactDisplayName",
  "studentCount",
  "guardianCount",
  "status",
  "updatedAt"
];

const defaultFamilyAccountFilters: FamilyAccountFilters = {
  status: "all",
  primaryContact: "all",
  lessonId: "all",
  students: "all",
  guardian: "all"
};

function statusPill(status: FamilyAccountStatus) {
  return <span className={`admin-list-pill admin-list-pill--${status}`}>{describeStatus(status)}</span>;
}

function compareFamilyRows(left: AdminFamilyAccountListRow, right: AdminFamilyAccountListRow, sort: FamilyAccountSortKey): number {
  switch (sort) {
    case "displayName":
      return compareAdminText(left.familyAccount.displayName, right.familyAccount.displayName);
    case "primaryContactDisplayName":
      return compareAdminText(formatRosterPrimaryContact(left.primaryContact), formatRosterPrimaryContact(right.primaryContact));
    case "studentCount":
      return left.students.length - right.students.length;
    case "guardianCount":
      return left.linkedGuardians.length - right.linkedGuardians.length;
    case "status":
      return compareAdminText(left.familyAccount.status, right.familyAccount.status);
    case "updatedAt":
      return compareAdminDate(left.familyAccount.updatedAt, right.familyAccount.updatedAt);
  }
}

function familyAccountDetailHref(tenantSlug: string, familyAccountId: string): string {
  return `/t/${tenantSlug}/teacher/admin/family-accounts/${familyAccountId}`;
}

function studentDetailHref(tenantSlug: string, studentId: string): string {
  return `/t/${tenantSlug}/teacher/admin/students/${studentId}`;
}

function relationshipWarningBadge(warnings: readonly string[]) {
  if (warnings.length === 0) return null;

  return (
    <span className="admin-list-warning-badge" title={warnings.join("\n")} aria-label={`要確認: ${warnings.join("。")}`}>
      要確認
    </span>
  );
}

function memberStatusBadge(status: "active" | "inactive" | null) {
  if (status === "inactive") {
    return <span className="admin-list-chip admin-list-chip--warning">無効メンバー</span>;
  }

  if (status === null) {
    return <span className="admin-list-chip admin-list-chip--muted">メンバー未登録</span>;
  }

  return null;
}

function renderFamilyAccountName(row: AdminFamilyAccountListRow, tenantSlug: string) {
  return (
    <div className="admin-list-relation">
      <strong>
        <Link to={familyAccountDetailHref(tenantSlug, row.familyAccount.familyAccountId)}>
          {row.familyAccount.displayName}
        </Link>
      </strong>
      {relationshipWarningBadge(row.dataQualityWarnings)}
    </div>
  );
}

function renderFamilyPrimaryContact(row: AdminFamilyAccountListRow) {
  return (
    <span className="admin-list-chip-list">
      <span
        className={`admin-list-chip${row.primaryContact && !row.primaryContact.hasActiveLinkToAnyFamilyStudent ? " admin-list-chip--warning" : ""}`}
        title={
          row.primaryContact && !row.primaryContact.hasActiveLinkToAnyFamilyStudent
            ? "代表保護者（連絡先）は家族内の生徒に紐づいていません"
            : undefined
        }
      >
        {row.primaryContact
          ? `${row.primaryContact.displayName}${row.primaryContact.hasActiveLinkToAnyFamilyStudent ? "" : "（生徒紐づけなし）"}`
          : "代表保護者（連絡先）未設定"}
      </span>
      {row.primaryContact ? memberStatusBadge(row.primaryContact.memberStatus) : null}
    </span>
  );
}

function renderFamilyGuardians(row: AdminFamilyAccountListRow, studentNamesById: ReadonlyMap<string, string>) {
  const guardianSummary = summarizeNames(row.linkedGuardians.map((guardian) => guardian.displayName), 2);

  if (row.linkedGuardians.length === 0) {
    return <span className="admin-list-chip admin-list-chip--muted">閲覧できる保護者なし</span>;
  }

  return (
    <span className="admin-list-chip-list" title={guardianSummary.fullText}>
      {row.linkedGuardians.slice(0, 2).map((guardian) => {
        const linkedStudentNames = guardian.linkedStudentIds
          .map((studentId) => studentNamesById.get(studentId) ?? null)
          .filter((name): name is string => Boolean(name));

        return (
          <span
            key={guardian.platformUserId}
            className={`admin-list-chip${guardian.memberStatus === "inactive" ? " admin-list-chip--warning" : ""}`}
            title={`紐づき先: ${linkedStudentNames.join("、") || "不明"}${guardian.isPrimaryContact ? " / 代表保護者（連絡先）" : ""}`}
          >
            {guardian.displayName}
            {guardian.isPrimaryContact ? "（代表連絡先）" : ""}
          </span>
        );
      })}
      {row.linkedGuardians.length > 2 ? (
        <span className="admin-list-chip admin-list-chip--muted">ほか{row.linkedGuardians.length - 2}名</span>
      ) : null}
    </span>
  );
}

function renderFamilyStudents(row: AdminFamilyAccountListRow, tenantSlug: string) {
  const studentSummary = summarizeNames(row.students.map((student) => student.displayName), 2);

  if (row.students.length === 0) {
    return <span className="admin-list-chip admin-list-chip--muted">生徒未設定</span>;
  }

  return (
    <span className="admin-list-chip-list" title={studentSummary.fullText}>
      {row.students.slice(0, 2).map((student) => (
        <Link key={student.studentId} className="admin-list-chip" to={studentDetailHref(tenantSlug, student.studentId)}>
          {student.displayName}
        </Link>
      ))}
      {row.students.length > 2 ? (
        <span className="admin-list-chip admin-list-chip--muted">ほか{row.students.length - 2}名</span>
      ) : null}
    </span>
  );
}

export function AdminFamilyAccountsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [students, setStudents] = useState<AdminStudentsResponseDto | null>(null);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentsResponseDto | null>(null);
  const [relations, setRelations] = useState<AdminRosterRelationsResponseDto | null>(null);
  const [lessons, setLessons] = useState<AdminLessonListItemDto[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const listState = useAdminListState<FamilyAccountSortKey, FamilyAccountFilters>({
    defaultSort: "displayName",
    defaultFilters: defaultFamilyAccountFilters,
    validSorts: familyAccountSortKeys,
    validateFilter: (key, value) => {
      if (key === "status") return (value === "active" || value === "inactive" ? value : "all") as FamilyAccountFilters[typeof key];
      if (key === "primaryContact") {
        return (value === "set" || value === "unset" || value === "notLinkedToAnyStudent" ? value : "all") as FamilyAccountFilters[typeof key];
      }
      if (key === "lessonId") return (value || "all") as FamilyAccountFilters[typeof key];
      if (key === "students") return (value === "has" || value === "none" ? value : "all") as FamilyAccountFilters[typeof key];
      if (key === "guardian") return (value === "linked" || value === "unlinked" ? value : "all") as FamilyAccountFilters[typeof key];
      return "all" as FamilyAccountFilters[typeof key];
    }
  });

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudents, nextFamilyAccounts, nextEnrollments, nextLessons, nextRelations] = await Promise.all([
        fetchAdminStudents(tenantSlug),
        fetchFamilyAccounts(tenantSlug),
        fetchAdminEnrollments(tenantSlug),
        fetchAdminLessons(tenantSlug),
        fetchAdminRosterRelations(tenantSlug)
      ]);

      setStudents(nextStudents);
      setFamilyAccounts(nextFamilyAccounts);
      setEnrollments(nextEnrollments);
      setLessons(nextLessons.items);
      setRelations(nextRelations);
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.familyAccount}一覧の取得に失敗しました`));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    void refreshPage();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  const allRows = useMemo(
    () =>
      buildFamilyAccountRows({
        familyAccounts: familyAccounts?.items ?? [],
        students: students?.items ?? [],
        enrollments: enrollments?.items ?? [],
        relations
      }),
    [enrollments, familyAccounts, relations, students]
  );

  const filteredRows = useMemo(() => {
    const filters = listState.filters;
    return allRows.filter((row) => {
      if (!adminSearchMatches(listState.appliedQuery, row.searchText)) return false;
      if (filters.status !== "all" && row.familyAccount.status !== filters.status) return false;
      if (filters.primaryContact === "set" && !row.familyAccount.primaryContactPlatformUserId) return false;
      if (filters.primaryContact === "unset" && row.familyAccount.primaryContactPlatformUserId) return false;
      if (filters.primaryContact === "notLinkedToAnyStudent" && (!row.primaryContact || row.primaryContact.hasActiveLinkToAnyFamilyStudent)) return false;
      if (filters.lessonId !== "all" && !row.currentLessonIds.includes(filters.lessonId)) return false;
      if (filters.students === "has" && row.students.length === 0) return false;
      if (filters.students === "none" && row.students.length > 0) return false;
      if (filters.guardian === "linked" && row.linkedGuardians.length === 0) return false;
      if (filters.guardian === "unlinked" && row.linkedGuardians.length > 0) return false;
      return true;
    });
  }, [allRows, listState.appliedQuery, listState.filters]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((left, right) => {
      const result = compareFamilyRows(left, right, listState.sort) || compareAdminText(left.familyAccount.familyAccountId, right.familyAccount.familyAccountId);
      return listState.order === "asc" ? result : -result;
    });
  }, [filteredRows, listState.order, listState.sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / listState.pageSize));
  const currentPage = Math.min(listState.page, pageCount);
  const pagedRows = sortedRows.slice((currentPage - 1) * listState.pageSize, currentPage * listState.pageSize);
  const filteredIds = new Set(filteredRows.map((row) => row.familyAccount.familyAccountId));
  const hiddenSelectedCount = [...selectedIds].filter((id) => !filteredIds.has(id)).length;
  const selectedRows = allRows.filter((row) => selectedIds.has(row.familyAccount.familyAccountId));
  const exportRows = selectedRows.length > 0 ? selectedRows : filteredRows;
  const lessonOptions = buildLessonFilterOptions(lessons);
  const studentNamesById = useMemo(
    () => new Map((students?.items ?? []).map((student) => [student.studentId, student.displayName] as const)),
    [students]
  );

  useEffect(() => {
    if (listState.page !== currentPage) listState.setPage(currentPage);
  }, [currentPage, listState]);

  function toggleCurrentPageSelection() {
    setSelectedIds((current) => {
      const next = new Set(current);
      const currentPageIds = pagedRows.map((row) => row.familyAccount.familyAccountId);
      const allSelected = currentPageIds.every((id) => next.has(id));
      for (const id of currentPageIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  async function handleBulkStatus(status: FamilyAccountStatus) {
    const count = selectedIds.size;
    if (count === 0) return;
    const label = status === "active" ? "有効" : "無効";
    const confirmed = window.confirm(
      `選択した${count}件の${tenantLabels.familyAccount}を「${label}」に変更します。\n紐付いている${tenantLabels.student}の状態は変更されません。`
    );

    if (!confirmed) return;

    setBulkSaving(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await bulkUpdateFamilyAccountStatus(tenantSlug, { familyAccountIds: [...selectedIds], status });
      setSelectedIds(new Set());
      setFeedbackMessage(`選択した${count}件の${tenantLabels.familyAccount}を更新しました。`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }
      setLoadError(getProtectedApiErrorMessage(error, "一括ステータス更新に失敗しました"));
    } finally {
      setBulkSaving(false);
    }
  }

  function handleExportCsv() {
    const content = buildCsvContent({
      rows: exportRows,
      columns: [
        { header: `${tenantLabels.familyAccount}名`, value: (row) => row.familyAccount.displayName },
        { header: "ステータス", value: (row) => describeStatus(row.familyAccount.status) },
        { header: "代表保護者（連絡先）", value: (row) => row.primaryContact?.displayName ?? "" },
        { header: "アプリで閲覧できる保護者", value: (row) => formatGuardianNames(row.linkedGuardians) },
        { header: `${tenantLabels.student}数`, value: (row) => row.students.length },
        { header: `${tenantLabels.student}名`, value: (row) => row.students.map((student) => student.displayName).join("／") },
        { header: `${tenantLabels.enrollment}中${tenantLabels.lesson}`, value: (row) => row.currentLessonTitles.join("／") },
        { header: "作成日時", value: (row) => row.familyAccount.createdAt },
        { header: "更新日時", value: (row) => row.familyAccount.updatedAt }
      ]
    });
    downloadCsv(`${tenantSlug}_family-accounts_${formatTenantDateForCsvFilename()}.csv`, content);
  }

  const columns: Array<AdminDataGridColumn<AdminFamilyAccountListRow>> = [
    { key: "displayName", header: tenantLabels.familyAccount, sortable: true, mobilePriority: "primary", render: (row) => renderFamilyAccountName(row, tenantSlug) },
    { key: "primaryContactDisplayName", header: "代表保護者（連絡先）", sortable: true, mobilePriority: "secondary", render: (row) => renderFamilyPrimaryContact(row) },
    { key: "guardianCount", header: "アプリで閲覧できる保護者", sortable: true, mobilePriority: "secondary", render: (row) => renderFamilyGuardians(row, studentNamesById) },
    { key: "studentCount", header: `${tenantLabels.student}数`, sortable: true, width: "100px", mobilePriority: "secondary", render: (row) => row.students.length },
    { key: "studentNames", header: tenantLabels.student, mobilePriority: "secondary", render: (row) => renderFamilyStudents(row, tenantSlug) },
    { key: "currentLessons", header: `所属中${tenantLabels.lesson}`, mobilePriority: "secondary", render: (row) => row.currentLessonTitles.join(" / ") || "なし" },
    { key: "status", header: "ステータス", sortable: true, width: "110px", mobilePriority: "primary", render: (row) => statusPill(row.familyAccount.status) },
    { key: "updatedAt", header: "更新日時", sortable: true, width: "150px", mobilePriority: "hidden", render: (row) => formatAdminDate(row.familyAccount.updatedAt) }
  ];

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.familyAccount}一覧`}
      description={`${tenantLabels.familyAccount}を検索・絞り込み・一括更新できます。登録は専用ページで行います。`}
      links={buildAdminRosterLinks(tenantLabels)}
      layoutVariant="wide"
    >
      <div className="admin-list-page">
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <AdminListToolbar
              title={`${tenantLabels.familyAccount}一覧`}
              totalCount={filteredRows.length}
              draftQuery={listState.draftQuery}
              appliedQuery={listState.appliedQuery}
              searchPlaceholder={`家族名・代表連絡先・閲覧保護者・${tenantLabels.student}・${tenantLabels.lesson}で検索`}
              csvButtonLabel={selectedIds.size > 0 ? "選択中をCSV出力" : "絞り込み結果をCSV出力"}
              csvDisabled={loadState === "loading" || exportRows.length === 0}
              createHref={`/t/${tenantSlug}/teacher/admin/family-accounts/new`}
              createLabel={`${tenantLabels.familyAccount}を登録`}
              onDraftQueryChange={listState.setDraftQuery}
              onSearchSubmit={listState.submitSearch}
              onExportCsv={handleExportCsv}
            >
              <AdminFilterPanel>
                <label>
                  <span>ステータス</span>
                  <select value={listState.filters.status} onChange={(event) => listState.setFilter("status", event.target.value as FamilyAccountFilters["status"])}>
                    <option value="all">すべて</option>
                    <option value="active">{describeStatus("active")}</option>
                    <option value="inactive">{describeStatus("inactive")}</option>
                  </select>
                </label>
                <label>
                  <span>代表保護者（連絡先）</span>
                  <select value={listState.filters.primaryContact} onChange={(event) => listState.setFilter("primaryContact", event.target.value as FamilyAccountFilters["primaryContact"])}>
                    <option value="all">すべて</option>
                    <option value="set">設定済み</option>
                    <option value="unset">未設定</option>
                    <option value="notLinkedToAnyStudent">生徒紐づけなし</option>
                  </select>
                </label>
                <label>
                  <span>{tenantLabels.lesson}</span>
                  <select value={listState.filters.lessonId} onChange={(event) => listState.setFilter("lessonId", event.target.value)}>
                    <option value="all">すべて</option>
                    {lessonOptions.map((lesson) => (
                      <option key={lesson.lessonId} value={lesson.lessonId}>{lesson.title}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{tenantLabels.student}</span>
                  <select value={listState.filters.students} onChange={(event) => listState.setFilter("students", event.target.value as FamilyAccountFilters["students"])}>
                    <option value="all">すべて</option>
                    <option value="has">生徒あり</option>
                    <option value="none">生徒なし</option>
                  </select>
                </label>
                <label>
                  <span>アプリで閲覧できる保護者</span>
                  <select value={listState.filters.guardian} onChange={(event) => listState.setFilter("guardian", event.target.value as FamilyAccountFilters["guardian"])}>
                    <option value="all">すべて</option>
                    <option value="linked">あり</option>
                    <option value="unlinked">なし</option>
                  </select>
                </label>
              </AdminFilterPanel>
            </AdminListToolbar>

            <AdminBulkActionBar
              selectedCount={selectedIds.size}
              hiddenSelectedCount={hiddenSelectedCount}
              saving={bulkSaving}
              disabled={loadState === "loading"}
              activeLabel="有効にする"
              inactiveLabel="無効にする"
              onBulkStatus={handleBulkStatus}
              onClearVisibleSelection={() => setSelectedIds((current) => new Set([...current].filter((id) => !filteredIds.has(id))))}
              onClearAllSelection={() => setSelectedIds(new Set())}
            />

            <AdminDataGrid
              columns={columns}
              rows={pagedRows}
              getRowId={(row) => row.familyAccount.familyAccountId}
              getRowLabel={(row) => row.familyAccount.displayName}
              getDetailHref={(row) => familyAccountDetailHref(tenantSlug, row.familyAccount.familyAccountId)}
              selectedIds={selectedIds}
              sortKey={listState.sort}
              sortOrder={listState.order}
              loading={loadState === "loading"}
              errorMessage={loadState === "error" ? loadError : null}
              emptyMessage={`${tenantLabels.familyAccount}は見つかりませんでした。`}
              onRetry={() => void refreshPage()}
              onToggleRow={(rowId) =>
                setSelectedIds((current) => {
                  const next = new Set(current);
                  if (next.has(rowId)) next.delete(rowId);
                  else next.add(rowId);
                  return next;
                })
              }
              onToggleCurrentPage={toggleCurrentPageSelection}
              onSort={(columnKey) => listState.setSort(columnKey as FamilyAccountSortKey)}
            />

            <AdminPagination
              page={currentPage}
              pageSize={listState.pageSize}
              totalCount={filteredRows.length}
              onPageChange={listState.setPage}
              onPageSizeChange={listState.setPageSize}
            />
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
